import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button, Chip, Dialog } from '@nyce/admin-kit/ui';
import { EmptyState, ErrorBox, Loading } from '../../components/common';
import { pods, type Pod, type PodEvent } from '../../api/pods';
import { errorMessage } from '../../lib/errors';
import { formatAge } from '../../lib/pods';

/** `GET /pods/events` for one pod, newest first. */
export function PodEventsDialog({
  environmentId,
  pod,
  onClose,
}: {
  environmentId: string;
  pod: Pod;
  onClose: () => void;
}) {
  const [events, setEvents] = useState<PodEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEvents(await pods.events(environmentId, pod.namespace, pod.name));
      setError(null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [environmentId, pod.namespace, pod.name]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={720}
      title={`Events · ${pod.name}`}
      footer={
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={14} />}
          loading={loading}
          onClick={() => void load()}
        >
          Refresh
        </Button>
      }
    >
      <ErrorBox error={error} />
      {!events && loading && <Loading loading />}
      {events && !events.length && (
        <EmptyState title="No events" hint="Kubernetes keeps events for about an hour." />
      )}
      {events && events.length > 0 && (
        <ul className="oph-events">
          {events.map((e, i) => (
            <li key={i} className="oph-event">
              <div className="oph-event-head">
                <Chip size="sm" className={`oph-tone nyk-tone-${e.type === 'Warning' ? 'amber' : 'grey'}`}>
                  {e.type}
                </Chip>
                <strong>{e.reason}</strong>
                {e.count > 1 && <span className="nyk-ui-hint">×{e.count}</span>}
                <span className="oph-event-time" title={e.lastSeen}>
                  {formatAge(e.lastSeen)} ago
                </span>
              </div>
              <div className="oph-event-msg">{e.message}</div>
              {e.object && <div className="nyk-ui-hint nyk-mono">{e.object}</div>}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
